import { getSettings } from '../modules/Settings/settings.api';
import { IStringValue, IUserCriteria } from '../types/types';

export const parseBooleanValue = (value: string): boolean => {
    const lowerCaseValue = value.trim().toLowerCase();
    return lowerCaseValue === "true" || lowerCaseValue === "1" || lowerCaseValue === "yes";
}

// Normalizes a Template/WorksheetColumn "Custom Name" edit so that a blank/whitespace-only
// value is treated as "no custom name" (null) rather than being persisted as an empty string.
// Without this, clearing the Custom Name field would save the worksheet/column Name as "",
// which breaks template document generation and leaves the Save button stuck showing the
// "Generate" spinner forever (AB#1141705).
export function normalizeCustomName(value: string | null | undefined): string | null {
    return value?.trim() ? value : null;
}

export function formatUSZipCode(zip: string): string {
    let cleanedZip = zip.trim();

    // Check if the zip code contains any non-digit characters
    if (/\D/.test(cleanedZip)) {
        return zip; // Return the original input if it contains non-digit characters
    }

    // Format the cleaned zip code
    if (cleanedZip.length > 5) {
        return cleanedZip.slice(0, 5) + '-' + cleanedZip.slice(5);
    } else {
        return zip; // Return the original input if not a valid zip code format
    }
}

export function formatPhoneNumber(phone: string | number | undefined): string {
    if (!phone) {
        return '';
    }

    let cleanedPhone = phone.toString().trim();

    // Check if the phone code contains any non-digit characters
    if (/\D/.test(cleanedPhone)) {
        return phone.toString(); // Return the original input if it contains non-digit characters
    }

    // Format the cleaned phone code
    if (cleanedPhone.length !== 10) {
        return cleanedPhone;
    }

    return cleanedPhone.slice(0, 3) + '-' + cleanedPhone.slice(3, 6) + '-' + cleanedPhone.slice(6, 10);
}

export const convertUTCToLocalTime = (dateString: string | number | Date) => {
    let date = new Date(dateString);
    let minDate = new Date("0001-01-01T00:00:00");

    if (date.toLocaleString() === minDate.toLocaleString())
        return "";

    const milliseconds = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
    );
    const localTime = new Date(milliseconds);
    return localTime.toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toUpperCase();
};

export const convertUTCToLocalDate = (dateString: string | number | Date) => {
    let date = new Date(dateString);
    let minDate = new Date("0001-01-01T00:00:00");

    if (date.toLocaleString() === minDate.toLocaleString())
        return "";

    const milliseconds = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
    );

    const localTime = new Date(milliseconds);
    return localTime.toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: '2-digit' }).toUpperCase();
};

export const convertToDate = (dateString: string | number | Date) => {
    let date = new Date(dateString);
    let minDate = new Date("0001-01-01T00:00:00");

    if (date.toLocaleString() === minDate.toLocaleString())
        return "";

    return date.toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: '2-digit' }).toUpperCase();
};

export function getEnumKeyByEnumValue<T extends { [index: string]: string }>(myEnum: T, enumValue: string): keyof T {
    let keys = Object.keys(myEnum).filter(x => myEnum[x] === enumValue);
    return keys.length > 0 ? keys[0] : 0;
};

export function padLeft(number: number, length: number, character: string) {
    var result = String(number);

    for (var i = result.length; i < length; ++i) {
        result = character + result;
    }

    return result;
};

export function format12hourDateTime(datetime: Date): string {
    let month = padLeft(datetime.getMonth() + 1, 2, '0');
    let day = padLeft(datetime.getDate(), 2, '0');
    let year = padLeft(datetime.getFullYear(), 2, '0');
    let hours = ((datetime.getHours() + 11) % 12 + 1);
    let minutes = padLeft(datetime.getMinutes(), 2, '0');
    let seconds = padLeft(datetime.getSeconds(), 2, '0');
    let meridian = datetime.getHours() >= 12 ? 'PM' : 'AM';

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${meridian}`
}

export const callGetStringsByKeys = async (criteria: string): Promise<IStringValue[] | null> => {
    try {
        const response = await fetch(`api/Common/GetStringsByKeys`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: criteria,
        });

        if (!response.ok) {
            throw new Error("Error calling GetStringsByKeys");
        }

        return response.json();
    }
	catch (error) {
		console.error(`callGetStringsByKeys - Failed to get strings by keys, error: ${error}`);
		return null;
	}
}

export const getInfoJsonWithArray = async (criteria: string[]): Promise<IStringValue[] | null> => {
    let response = await callGetStringsByKeys(JSON.stringify(criteria))

    return response;
}

export const GetStringValue = async (key: string): Promise<IStringValue | null> => {
	console.log(`Getting string value for key "${key}"...`);
    let response = await callGetStringsByKeys(JSON.stringify([key]))

    let returnValue: IStringValue | null = null;

    if (response) {
        response.forEach((content: IStringValue) => {
            switch (content.key) {
                case key:
                    returnValue = content;
                    break;
                default:
                    break;
            }

        });
    }

    return returnValue;
}


export const transformHelpUrl = (helpURL: string, isInternalUser: boolean): string => {

    if (isInternalUser) {
        helpURL = helpURL.replace("{{additionalQueryParameters}}", '?f=Audience%3AInternal')
    }

    else {
        helpURL = helpURL.replace("{{additionalQueryParameters}}", '?f=Audience%3AInternal%20!')
    }

    return helpURL;

}

export async function getClientSettings(): Promise<any> {
    const response = await fetch('api/Common/GetClientSettings');
    if (!response.ok) {
        throw new Error(`Failed to fetch client settings: ${response.statusText}`);
    }
    return await response.json();
}
export function interpolate(template: string, values: Record<string, string>): string {
    return template.replace(/{(\w+)}/g, (_, key) => values[key] ?? `{${key}}`);
}

